import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6fmuo8r225iqdhs0dd0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-cream-light pb-16 lg:pb-0">
      <Header />

      {/* Header Section */}
      <motion.section
        ref={headerRef}
        initial={{ opacity: 0, y: 30 }}
        animate={headerInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="pt-32 pb-12"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="font-serif text-4xl lg:text-6xl font-bold text-navy mb-4">
            İLETİŞİM
          </h1>
          <p className="font-sans text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
            Projeleriniz, fikirleriniz veya iş birliği fırsatları için benimle iletişime geçin
          </p>
        </div>
      </motion.section>

      {/* Contact Form Section */}
      <motion.section
        ref={formRef}
        initial={{ opacity: 0, y: 50 }}
        animate={formInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="pb-20"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="mb-5">
                    <label htmlFor="name" className="font-sans text-sm font-medium text-navy mb-2 block">
                      Ad Soyad
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>

                  <div className="mb-5">
                    <label htmlFor="email" className="font-sans text-sm font-medium text-navy mb-2 block">
                      E-posta
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                      placeholder="ornek@email.com"
                    />
                  </div>

                  <div className="mb-5">
                    <label htmlFor="subject" className="font-sans text-sm font-medium text-navy mb-2 block">
                      Konu
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                      placeholder="Mesajınızın konusu"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="font-sans text-sm font-medium text-navy mb-2 block">
                      Mesaj
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors duration-300 resize-vertical"
                      placeholder="Mesajınızı buraya yazın (maksimum 500 karakter)"
                    ></textarea>
                    <p className="font-sans text-xs text-gray-500 mt-1">
                      {formData.message.length}/500 karakter
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-navy text-white font-sans font-semibold py-4 rounded-lg hover:bg-gold transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="font-sans text-sm text-green-700">
                        Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağım.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="font-sans text-sm text-red-700">
                        Bir hata oluştu. Lütfen daha sonra tekrar deneyin.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Email Card */}
              <div className="bg-cream rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className="ri-mail-line text-4xl text-gold"></i>
                </div>
                <h3 className="font-serif text-xl font-bold text-navy mb-3">
                  E-posta
                </h3>
                <a
                  href="mailto:info@fatihsinanyilmaz.com"
                  className="font-sans text-base text-gray-600 hover:text-gold transition-colors duration-300 cursor-pointer"
                >
                  info@fatihsinanyilmaz.com
                </a>
              </div>

              {/* Social Media Card */}
              <div className="bg-cream rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className="ri-share-line text-4xl text-gold"></i>
                </div>
                <h3 className="font-serif text-xl font-bold text-navy mb-4">
                  Sosyal Medya
                </h3>
                <div className="space-y-3">
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center font-sans text-base text-gray-600 hover:text-gold transition-colors duration-300 cursor-pointer"
                  >
                    <i className="ri-instagram-line text-xl mr-3"></i>
                    Instagram
                  </a>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center font-sans text-base text-gray-600 hover:text-gold transition-colors duration-300 cursor-pointer"
                  >
                    <i className="ri-linkedin-line text-xl mr-3"></i>
                    LinkedIn
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center font-sans text-base text-gray-600 hover:text-gold transition-colors duration-300 cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-xl mr-3"></i>
                    Twitter
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
};

export default ContactPage;